import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaCheck, FaTimes } from 'react-icons/fa';
import { getPropuestasCuenta, aprobarPropuestaCuenta, rechazarPropuestaCuenta } from '../services/adminApi';
import { showError, showSuccess, confirmarAprobacionCuenta, pedirMotivoRechazo } from '../utils/alerts';
import '../styles/pages/PlanCuentasPage.css';

const FILTROS = [
  { valor: 'PENDIENTE', label: 'Pendientes' },
  { valor: 'APROBADA', label: 'Aprobadas' },
  { valor: 'RECHAZADA', label: 'Rechazadas' },
  { valor: 'TODAS', label: 'Todas' },
];

const TIPO_LABELS = {
  ACTIVO: 'Activo',
  PASIVO: 'Pasivo',
  GANANCIA: 'Ganancia',
  PERDIDA: 'Pérdida',
};

const ESTADO_LABELS = {
  PENDIENTE: 'Pendiente',
  APROBADA: 'Aprobada',
  RECHAZADA: 'Rechazada',
};

const fmtFecha = (valor) => (valor
  ? new Date(valor).toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Santiago',
  })
  : '');

/**
 * Panel de EFE: propuestas de cuentas nuevas del módulo Plan de cuentas. Aprobar crea la
 * cuenta con el siguiente código de su partida para todas las empresas; rechazar pide un
 * motivo que ve quien la propuso. Las propuestas no se editan.
 */
const AdminPlanCuentasPage = () => {
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState('PENDIENTE');
  const [propuestas, setPropuestas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const respuesta = await getPropuestasCuenta(filtro);
      setPropuestas(respuesta.data);
    } catch (error) {
      showError('No se pudieron obtener las propuestas', error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => { cargar(); }, [cargar]);

  const aprobar = async (p) => {
    if (!(await confirmarAprobacionCuenta(p.nombre_cuenta, p.codigo_siguiente))) return;
    setProcesando(p.id);
    try {
      const respuesta = await aprobarPropuestaCuenta(p.id);
      showSuccess(`${respuesta.data.nombre_cuenta} quedó creada con el código ${respuesta.data.codigo}`);
      await cargar();
    } catch (error) {
      showError('No se pudo aprobar la propuesta', error.response?.data?.message);
      await cargar();
    } finally {
      setProcesando(null);
    }
  };

  const rechazar = async (p) => {
    const motivo = await pedirMotivoRechazo(p.nombre_cuenta);
    if (!motivo) return;
    setProcesando(p.id);
    try {
      await rechazarPropuestaCuenta(p.id, motivo);
      showSuccess(`Propuesta de ${p.nombre_cuenta} rechazada`);
      await cargar();
    } catch (error) {
      showError('No se pudo rechazar la propuesta', error.response?.data?.message);
      await cargar();
    } finally {
      setProcesando(null);
    }
  };

  return (
    <div className="pc-admin-page">
      <header className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <button className="btn-back" onClick={() => navigate('/admin/clientes')}>← Volver</button>
            <h1>Propuestas de cuentas</h1>
          </div>
        </div>
      </header>

      <div className="pc-page">
        <div className="pc-filtros">
          <p className="pc-nota pc-admin-nota">
            Antes de aprobar, deja el sistema listo para la cuenta (por ejemplo, el F22): desde que se aprueba,
            cualquier empresa puede usarla.
          </p>
          <select className="pc-input pc-admin-filtro" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
            {FILTROS.map((f) => <option key={f.valor} value={f.valor}>{f.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="pc-loading"><FaSpinner className="pc-spinner" /> Cargando...</div>
        ) : propuestas.length === 0 ? (
          <p className="pc-vacio">No hay propuestas {filtro === 'TODAS' ? '' : FILTROS.find((f) => f.valor === filtro).label.toLowerCase()}</p>
        ) : (
          <div className="pc-tabla-wrapper">
            <table className="pc-tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Propuesta por</th>
                  <th>Cuenta</th>
                  <th>Partida</th>
                  <th>Tipo</th>
                  <th>F22</th>
                  <th>Motivo</th>
                  <th>Estado</th>
                  <th>Código</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {propuestas.map((p) => (
                  <tr key={p.id}>
                    <td className="pc-fecha">{fmtFecha(p.propuesto_en)}</td>
                    <td>
                      {p.propuesto_por_nombre}
                      <div className="pc-nota">{p.propuesto_por_correo}</div>
                    </td>
                    <td className="pc-cuenta-nombre">{p.nombre_cuenta}</td>
                    <td><span className="pc-codigo">{p.codigo_sii}</span> {p.partida}</td>
                    <td>{TIPO_LABELS[p.tipo_cuenta] || p.tipo_cuenta}</td>
                    <td>
                      {p.afecta_f22 ? 'Sí' : 'No'}
                      {p.concepto_f22 && <div className="pc-nota">{p.concepto_f22} ({p.codigos_f22})</div>}
                    </td>
                    <td>{p.motivo}</td>
                    <td>
                      <span className={`pc-estado pc-estado--${p.estado.toLowerCase()}`}>{ESTADO_LABELS[p.estado] || p.estado}</span>
                      {p.estado !== 'PENDIENTE' && (
                        <div className="pc-nota">{p.revisado_por_nombre} · {fmtFecha(p.revisado_en)}</div>
                      )}
                      {p.motivo_rechazo && <div className="pc-nota">{p.motivo_rechazo}</div>}
                    </td>
                    <td className="pc-codigo">
                      {p.codigo_asignado || (p.estado === 'PENDIENTE' ? (p.codigo_siguiente || 'Sin códigos libres') : '')}
                    </td>
                    <td>
                      {p.estado === 'PENDIENTE' && (
                        <div className="pc-acciones">
                          <button
                            className="pc-btn pc-btn--chico pc-btn--primary"
                            onClick={() => aprobar(p)}
                            disabled={procesando !== null || !p.codigo_siguiente}
                          >
                            {procesando === p.id ? <FaSpinner className="pc-spinner" /> : <FaCheck />} Aprobar
                          </button>
                          <button
                            className="pc-btn pc-btn--chico"
                            onClick={() => rechazar(p)}
                            disabled={procesando !== null}
                          >
                            <FaTimes /> Rechazar
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPlanCuentasPage;
